import { useState, useCallback } from 'react';
import { unzipSync } from 'fflate';

// Bambu Lab A1 build volume in mm
const MAX_BUILD_MM = 256;

interface ParsedVolume {
  readonly volumeCm3: number;
  readonly boundingBox: {
    readonly width: number;
    readonly height: number;
    readonly depth: number;
  };
}

interface VolumeEstimate extends ParsedVolume {
  readonly exceedsBuildVolume: boolean;
}

export const useModelVolume = () => {
  const [estimate, setEstimate] = useState<VolumeEstimate | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const calculate = useCallback(async (file: File): Promise<VolumeEstimate | null> => {
    if (!file.name.toLowerCase().endsWith('.3mf')) {
      return null;
    }

    setIsCalculating(true);

    try {
      const buffer = await file.arrayBuffer();
      const parsed = parse3mfVolume(new Uint8Array(buffer));

      // Check if any dimension exceeds the build plate (bounding box is in cm)
      const exceedsBuildVolume =
        parsed.boundingBox.width * 10 > MAX_BUILD_MM ||
        parsed.boundingBox.height * 10 > MAX_BUILD_MM ||
        parsed.boundingBox.depth * 10 > MAX_BUILD_MM;

      const result = { ...parsed, exceedsBuildVolume };
      setEstimate(result);
      return result;
    } catch {
      setEstimate(null);
      return null;
    } finally {
      setIsCalculating(false);
    }
  }, []);

  const reset = useCallback(() => {
    setEstimate(null);
  }, []);

  return { estimate, isCalculating, calculate, reset };
};

/**
 * Client-side 3MF parser for rough volume estimation.
 * 3MF is a ZIP archive containing XML with mesh data.
 * Bambu Studio 3MF files may split objects into separate .model files.
 */
const parse3mfVolume = (data: Uint8Array): ParsedVolume => {
  const zip = unzipSync(data);

  // Find ALL .model files (Bambu Studio splits objects into 3D/Objects/*.model)
  const modelKeys = Object.keys(zip).filter((name) =>
    name.toLowerCase().endsWith('.model'),
  );

  if (modelKeys.length === 0) {
    throw new Error('No .model file found in 3MF archive');
  }

  // Parse vertices and triangles from all model files
  const vertices: number[][] = [];
  const triangles: { v1: number; v2: number; v3: number }[] = [];

  for (const modelKey of modelKeys) {
    const xml = new TextDecoder().decode(zip[modelKey]);
    parseMeshFromXml(xml, vertices, triangles);
  }

  if (vertices.length === 0 || triangles.length === 0) {
    throw new Error('3MF file contains no mesh data');
  }

  // Compute volume and bounding box
  let volume = 0;
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

  for (const [x, y, z] of vertices) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (z < minZ) minZ = z;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    if (z > maxZ) maxZ = z;
  }

  for (const tri of triangles) {
    const [v1x, v1y, v1z] = vertices[tri.v1];
    const [v2x, v2y, v2z] = vertices[tri.v2];
    const [v3x, v3y, v3z] = vertices[tri.v3];

    volume +=
      (v1x * (v2y * v3z - v3y * v2z) -
        v2x * (v1y * v3z - v3y * v1z) +
        v3x * (v1y * v2z - v2y * v1z)) /
      6.0;
  }

  // 3MF coordinates are in mm
  const volumeCm3 = Math.abs(volume) / 1000; // mm3 -> cm3

  return {
    volumeCm3: Math.round(volumeCm3 * 100) / 100,
    boundingBox: {
      width: Math.round(((maxX - minX) / 10) * 100) / 100,  // mm -> cm
      height: Math.round(((maxY - minY) / 10) * 100) / 100,
      depth: Math.round(((maxZ - minZ) / 10) * 100) / 100,
    },
  };
};

/** Extract vertices and triangles from a single .model XML string */
const parseMeshFromXml = (
  xml: string,
  vertices: number[][],
  triangles: { v1: number; v2: number; v3: number }[],
): void => {
  // Use indexOf to find <mesh>...</mesh> blocks (regex overflows on large files)
  const meshOpenTag = '<mesh>';
  const meshCloseTag = '</mesh>';
  let searchFrom = 0;

  while (true) {
    const meshStart = xml.indexOf(meshOpenTag, searchFrom);
    if (meshStart === -1) break;
    const contentStart = meshStart + meshOpenTag.length;
    const meshEnd = xml.indexOf(meshCloseTag, contentStart);
    if (meshEnd === -1) break;

    const meshContent = xml.substring(contentStart, meshEnd);
    searchFrom = meshEnd + meshCloseTag.length;

    const vertexOffset = vertices.length;

    const vertexRegex = /<vertex\s+x="([^"]+)"\s+y="([^"]+)"\s+z="([^"]+)"\s*\/>/g;
    let vMatch: RegExpExecArray | null;
    while ((vMatch = vertexRegex.exec(meshContent)) !== null) {
      vertices.push([
        parseFloat(vMatch[1]),
        parseFloat(vMatch[2]),
        parseFloat(vMatch[3]),
      ]);
    }

    const triRegex = /<triangle\s+v1="(\d+)"\s+v2="(\d+)"\s+v3="(\d+)"[^/]*\/>/g;
    let tMatch: RegExpExecArray | null;
    while ((tMatch = triRegex.exec(meshContent)) !== null) {
      triangles.push({
        v1: parseInt(tMatch[1], 10) + vertexOffset,
        v2: parseInt(tMatch[2], 10) + vertexOffset,
        v3: parseInt(tMatch[3], 10) + vertexOffset,
      });
    }
  }
};
